import { routeParams } from '@/routes';
import { loadPresentation } from '@/utils/load-presentation';
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Reveal from 'reveal.js';
import type { RevealApi } from 'reveal.js';
import RevealNotes from 'reveal.js/plugin/notes';
import { PresentationContext } from './core/PresentationContext';
import { DebugOverlay } from './debug/DebugOverlay';
import { PerformanceMonitor } from './debug/PerformanceMonitor';
import { ErrorWarning } from './ErrorWarning';
import { NetworkBackground } from './NetworkBackground';
import { AudioVisualizer } from './visualizers/AudioVisualizer';
import { VideoPoolProvider } from '@/utils/VideoPoolContext';
import { resetPerformanceTracker } from '@/utils/performanceMetrics';

export const Presentation = () => {
  const { [routeParams.folder]: folder } = useParams<{ [routeParams.folder]: string }>();
  const navigate = useNavigate();
  const [slides, setSlides] = useState<(() => ReactElement)[]>();
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [error, setError] = useState<string>();
  const [showPerformanceMonitor, setShowPerformanceMonitor] = useState(false);

  const deckDivRef = useRef<HTMLDivElement>(null);
  const deckRef = useRef<RevealApi | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setShowExitDialog((prev) => !prev);
      } else if (event.key === 'Enter' && showExitDialog) {
        navigate('/');
      } else if (event.ctrlKey && event.shiftKey && event.key === 'P') {
        event.preventDefault();
        setShowPerformanceMonitor((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, showExitDialog]);

  // Clean up performance tracker when component unmounts
  useEffect(() => {
    return () => {
      resetPerformanceTracker();
    };
  }, []); // Empty dependency array means this only runs on mount/unmount

  useEffect(() => {
    // Ignore a slow earlier load once the folder has changed or the component unmounted
    let cancelled = false;

    const loadSlides = async () => {
      if (!folder) return;

      try {
        const loadedSlides = await loadPresentation(folder);
        if (cancelled) return;
        setSlides(loadedSlides);
        setError(undefined);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load presentation');
        setSlides(undefined);
      }
    };

    loadSlides();

    return () => {
      cancelled = true;
    };
  }, [folder]);

  useEffect(() => {
    // The cleanup below destroys the previous deck, also when the folder changes
    let cancelled = false;

    const initDeck = async () => {
      if (!deckDivRef.current) {
        return;
      }

      const deck = new Reveal(deckDivRef.current);

      await deck.initialize({
        controls: false,
        progress: false,
        center: true,
        hash: true,
        hashOneBasedIndex: true,
        transition: 'none',
        plugins: [RevealNotes],
        width: '100%',
        height: '100%',
        overview: false,
        // Slides use fixed viewport geometry; Reveal's mobile scroll mode cannot lay them out.
        scrollActivationWidth: 0,
      });

      // Cleanup ran while initializing, so this deck is already stale
      if (cancelled) {
        deck.destroy();
        return;
      }

      deckRef.current = deck;
      setIsInitialized(true);

      // Make the deck reachable for the notes popup
      (window as Window & { Reveal?: RevealApi }).Reveal = deck;

      // Trigger initial slide check
      const currentSlide = deck.getCurrentSlide();
      if (currentSlide) {
        deck.dispatchEvent({
          target: currentSlide,
          type: 'slidechanged',
          data: {
            currentSlide,
            previousSlide: null,
            indexh: deck.getIndices().h,
            indexv: deck.getIndices().v,
          },
          bubbles: true,
        });
      }
    };

    // Initialize deck
    initDeck().catch(console.error);

    return () => {
      cancelled = true;
      if (deckRef.current) {
        deckRef.current.destroy();
        deckRef.current = null;
      }
      setIsInitialized(false);
      // Clean up the global reference
      delete (window as Window & { Reveal?: RevealApi }).Reveal;
    };
  }, [slides]);

  useEffect(() => {
    const deck = deckRef.current;
    if (!deck) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const scheduleAutoNext = () => {
      clearTimeout(timeoutId);

      const autoNextDuration = deck.getCurrentSlide()?.dataset.autoNext;
      if (autoNextDuration) {
        timeoutId = setTimeout(() => deck.next(), parseInt(autoNextDuration, 10));
      }
    };

    // Read the slide from the deck: reveal.js types slidechanged as a plain Event
    deck.on('slidechanged', scheduleAutoNext);

    // Check initial slide
    scheduleAutoNext();

    return () => {
      clearTimeout(timeoutId);
      deck.off('slidechanged', scheduleAutoNext);
    };
  }, [isInitialized]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Error</h1>
          <p className="text-gray-400 mb-8">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 rounded-sm bg-blue-600 hover:bg-blue-500 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <VideoPoolProvider maxSize={5}>
      <PresentationContext.Provider value={{ deck: deckRef.current, isInitialized }}>
        <div className="reveal" ref={deckDivRef}>
          <div className="slides">
            {slides?.map((Slide, index) => (
              <Slide key={index} />
            ))}
          </div>
        </div>
        {isInitialized && deckRef.current && (
          <>
            <NetworkBackground deck={deckRef.current} />
            <AudioVisualizer deck={deckRef.current} />
            <DebugOverlay />
            <PerformanceMonitor isVisible={showPerformanceMonitor} />
          </>
        )}
        {showExitDialog && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
            <div className="bg-gray-800 p-6 rounded-lg shadow-xl text-white">
              <h2 className="text-xl font-semibold mb-4">Exit Presentation?</h2>
              <p className="mb-6">Press Enter to return home or Escape to continue presenting.</p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowExitDialog(false)}
                  className="px-4 py-2 rounded-sm bg-gray-700 hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="px-4 py-2 rounded-sm bg-blue-600 hover:bg-blue-500 transition-colors"
                >
                  Home
                </button>
              </div>
            </div>
          </div>
        )}
        <ErrorWarning />
      </PresentationContext.Provider>
    </VideoPoolProvider>
  );
};
